import styled from '@emotion/styled'
import Image from 'next/image'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { usePrevious } from 'react-use'

import { TickIcon } from '../icons'
import { SAMPLE_BACKGROUND_IMAGES } from './constants'
import { FormValues } from 'components/forms/ThemeForm'

const SampleImageSelection = styled.ul`
	display: flex;
	gap: ${({ theme }) => theme.spaces.space4};
	margin-top: ${({ theme }) => theme.spaces.space2};
	margin-bottom: ${({ theme }) => theme.spaces.space4};
	list-style: none;
	padding: 0;

	@media (max-width: 900px) {
		min-height: 40px;
		max-width: 35vw;
		overflow-x: auto;
	}
`

const SampleImage = styled.button<{ selected: boolean }>`
	appearance: none;
	border: none;
	cursor: pointer;
	padding: 0;
	position: relative;
	height: 36px;
	width: 36px;
	border-radius: ${({ theme, selected }) => (selected ? theme.borderRadiuses.round : theme.borderRadiuses.sm)};
	overflow: hidden;
	transition: border-radius 0.2s;

	img {
		object-fit: cover;
	}
`

const TickWrapper = styled.span`
	position: absolute;
	inset: 0;
	margin: auto;
	height: 24px;
	width: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: ${({ theme }) => theme.colors.light.surface};
	border-radius: ${({ theme }) => theme.borderRadiuses.round};

	svg {
		fill: ${({ theme }) => theme.colors.light.primaryTextColor};
	}
`

const BackgroundImageSamplesSelection = () => {
	const { watch, setValue } = useFormContext<FormValues>()
	const currentThemeVariant = watch('themeVariant')
	const previousThemeVariant = usePrevious(currentThemeVariant)
	const currentHeaderBackgroundType = watch('headerBackgroundType')
	const currentHeaderBackgroundImage = watch('headerBackgroundImage')
	const sampleBackgroundImages = SAMPLE_BACKGROUND_IMAGES[currentThemeVariant]

	React.useEffect(() => {
		if (
			previousThemeVariant &&
			previousThemeVariant !== currentThemeVariant &&
			currentHeaderBackgroundType === 'image'
		) {
			const imageIndex = SAMPLE_BACKGROUND_IMAGES[previousThemeVariant].findIndex(
				(image) => image === currentHeaderBackgroundImage,
			)
			if (imageIndex !== -1) {
				setValue('headerBackgroundImage', SAMPLE_BACKGROUND_IMAGES[currentThemeVariant][imageIndex], {
					shouldValidate: true,
					shouldDirty: true,
				})
			}
		}
	}, [currentThemeVariant, previousThemeVariant, currentHeaderBackgroundImage, currentHeaderBackgroundType, setValue])

	function handleSampleImageSelect(index: number) {
		setValue('headerBackgroundImage', sampleBackgroundImages[index], { shouldValidate: true, shouldDirty: true })
		setValue('headerBackgroundType', 'image', { shouldValidate: true, shouldDirty: true })
	}

	return (
		<SampleImageSelection>
			{sampleBackgroundImages.map((image, index) => (
				<li key={index}>
					<SampleImage
						type="button"
						selected={image === currentHeaderBackgroundImage}
						onClick={() => handleSampleImageSelect(index)}
						aria-label={`select sample background no. ${index + 1}`}
					>
						<Image src={image} alt={`sample background no. ${index + 1}`} width={36} height={36} />
						{image === currentHeaderBackgroundImage && (
							<TickWrapper>
								<TickIcon />
							</TickWrapper>
						)}
					</SampleImage>
				</li>
			))}
		</SampleImageSelection>
	)
}

export default BackgroundImageSamplesSelection
